import { createSelector } from 'reselect';
import { getStateAtNamespaceKey } from 'redux/get-state-at-namespace-key';
import type { MoreOfWhatYouLoveState, MoreOfWhatYouLoveSuccessState } from '../types';

const EMPTY_SUGGESTIONS_ARRAY: never[] = [];
const EMPTY_CATEGORIES_ARRAY: never[] = [];

const isSuggestionsSuccessfullyLoaded = (
  state: MoreOfWhatYouLoveState,
): state is MoreOfWhatYouLoveSuccessState => 'suggestions' in state;

const getMoreOfWhatYouLove = (state: WtrState) =>
  getStateAtNamespaceKey(state, 'moreOfWhatYouLove');

export const getSuggestions = createSelector(getMoreOfWhatYouLove, moreOfWhatYouLove =>
  isSuggestionsSuccessfullyLoaded(moreOfWhatYouLove)
    ? moreOfWhatYouLove.suggestions
    : EMPTY_SUGGESTIONS_ARRAY,
);

export const getSuggestionsLength = createSelector(
  getSuggestions,
  suggestions => suggestions.length,
);

export const getSuggestionsLoading = createSelector(
  getMoreOfWhatYouLove,
  moreOfWhatYouLove => moreOfWhatYouLove.loading ?? true,
);

export const getSuggestionsLoadingMore = createSelector(getMoreOfWhatYouLove, moreOfWhatYouLove =>
  isSuggestionsSuccessfullyLoaded(moreOfWhatYouLove) ? moreOfWhatYouLove.loadingMore : false,
);

export const hasMoreSuggestions = createSelector(getMoreOfWhatYouLove, moreOfWhatYouLove =>
  isSuggestionsSuccessfullyLoaded(moreOfWhatYouLove) ? moreOfWhatYouLove.hasNextPage : false,
);

export const getSuggestionsPageNumber = createSelector(getMoreOfWhatYouLove, moreOfWhatYouLove =>
  isSuggestionsSuccessfullyLoaded(moreOfWhatYouLove) ? moreOfWhatYouLove.pageNumber : 1,
);

export const getSuggestionsOffersAndPromosOnly = createSelector(
  getMoreOfWhatYouLove,
  moreOfWhatYouLove =>
    isSuggestionsSuccessfullyLoaded(moreOfWhatYouLove)
      ? moreOfWhatYouLove.offersAndPromosOnly
      : false,
);

export const getSuggestionsHasOffersOrPromos = createSelector(
  getMoreOfWhatYouLove,
  moreOfWhatYouLove =>
    isSuggestionsSuccessfullyLoaded(moreOfWhatYouLove)
      ? moreOfWhatYouLove.hasOffersOrPromos
      : false,
);

export const getSuggestionsGroupedByCategory = createSelector(
  getMoreOfWhatYouLove,
  moreOfWhatYouLove => {
    const categories = isSuggestionsSuccessfullyLoaded(moreOfWhatYouLove)
      ? moreOfWhatYouLove.categories
      : EMPTY_CATEGORIES_ARRAY;

    return new Map(
      categories.map(category => [
        category.id,
        category.products.map(id => ({
          searchProduct: id,
        })),
      ]),
    );
  },
);
